import { useState, useEffect, useRef, useCallback } from 'react';
import * as Ably from 'ably';
import Confetti from './Confetti';

const API           = process.env.REACT_APP_API_URL || '/api';
const ABLY_AUTH_URL = `${API}/ably/token`;

// ─── Shared Ably client (same pattern as GeoGuessBoard) ───────────────────────
let _ablyClient = null;
function getAblyClient() {
  if (!_ablyClient) _ablyClient = new Ably.Realtime({ authUrl: ABLY_AUTH_URL, autoConnect: true });
  return _ablyClient;
}

function useAblyChannel(channelName, handlers) {
  const handlersRef = useRef(handlers);
  useEffect(() => { handlersRef.current = handlers; });
  useEffect(() => {
    if (!channelName) return;
    const client  = getAblyClient();
    if (!client) return;
    const channel = client.channels.get(channelName);
    const subs = Object.keys(handlersRef.current).map(event => {
      const cb = (msg) => handlersRef.current[event]?.(msg.data);
      channel.subscribe(event, cb);
      return { event, cb };
    });
    return () => subs.forEach(({ event, cb }) => channel.unsubscribe(event, cb));
  }, [channelName]);
}

// ─── Bracket math (mirrors bracketEngine) ─────────────────────────────────────
const ROUND_NAMES = {
  32: ['Round of 32', 'Round of 16', 'Quarter-finals', 'Semi-finals', 'Final'],
  16: ['Round of 16', 'Quarter-finals', 'Semi-finals', 'Final'],
  8:  ['Quarter-finals', 'Semi-finals', 'Final'],
  4:  ['Semi-finals', 'Final'],
  2:  ['Final'],
};

function roundCounts(teamCount) {
  const counts = [];
  for (let n = teamCount / 2; n >= 1; n = n / 2) counts.push(n);
  return counts;
}
function roundStarts(teamCount) {
  const starts = []; let acc = 0;
  for (const c of roundCounts(teamCount)) { starts.push(acc); acc += c; }
  return starts;
}
function roundOfMatch(teamCount, idx) {
  const starts = roundStarts(teamCount);
  for (let r = starts.length - 1; r >= 0; r--) if (idx >= starts[r]) return r;
  return 0;
}
function feeders(teamCount, idx) {
  const r = roundOfMatch(teamCount, idx);
  if (r === 0) return null;
  const starts = roundStarts(teamCount);
  const j = idx - starts[r];
  return [starts[r - 1] + 2 * j, starts[r - 1] + 2 * j + 1];
}
function participantsOf(fixture, revealed, idx) {
  const f = feeders(fixture.teamCount, idx);
  if (!f) return { home: fixture.entries[2 * idx] || null, away: fixture.entries[2 * idx + 1] || null };
  return { home: revealed?.[f[0]]?.winner || null, away: revealed?.[f[1]]?.winner || null };
}
function teamOf(fixture, code) {
  const t = fixture?.teams?.[code];
  return { code, name: t?.name || code || '—', flag: t?.flag || '🏳️' };
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  .bk-root {
    height: 100vh; width: 100vw; overflow: hidden;
    background:
      radial-gradient(1100px 480px at 50% -8%, #14294d 0%, transparent 60%),
      radial-gradient(900px 420px at 50% 112%, #0d2418 0%, transparent 55%),
      #060d1a;
    font-family: 'Barlow', sans-serif; color: #eaf1fb;
    display: flex; flex-direction: column; position: relative;
  }
  @keyframes bkFadeIn   { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes bkPop      { 0%{transform:scale(.82);opacity:0} 60%{transform:scale(1.06)} 100%{transform:scale(1);opacity:1} }
  @keyframes bkPulse    { 0%,100%{opacity:.65} 50%{opacity:1} }
  @keyframes bkBounce   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
  @keyframes bkSpin     { from{transform:rotate(0)} to{transform:rotate(360deg)} }
  @keyframes bkGlow     { 0%,100%{box-shadow:0 0 10px rgba(255,179,0,.25)} 50%{box-shadow:0 0 26px rgba(255,179,0,.6)} }
  @keyframes bkSlideIn  { from{opacity:0;transform:translateX(-10px) scale(.96)} to{opacity:1;transform:translateX(0) scale(1)} }
  @keyframes bkToast    { from{opacity:0;transform:translate(-50%,14px) scale(.92)} to{opacity:1;transform:translate(-50%,0) scale(1)} }
  .bk-match {
    background: rgba(14,27,48,.88); border: 1px solid #20365c; border-radius: 6px;
    overflow: hidden; width: 100%; display: flex;
  }
  .bk-pos {
    flex: 0 0 auto; display: flex; align-items: center; justify-content: center;
    min-width: clamp(13px,1.2vw,28px); padding: 0 2px;
    font-size: clamp(8px,.8vw,15px); font-weight: 800; line-height: 1;
    color: #3f5578; background: rgba(255,255,255,.03);
    border-right: 1px solid #20365c; font-variant-numeric: tabular-nums;
  }
  .bk-match.clm { border-color: rgba(180,140,255,.5); }
  .bk-match.clm .bk-pos { color: #b48cff; }
  .bk-row {
    display: flex; align-items: center; gap: 6px;
    padding: 3px 7px; font-size: clamp(10px,1.05vw,21px); font-weight: 700;
    letter-spacing: .04em; min-height: clamp(16px,2.2vh,36px);
  }
  .bk-row + .bk-row { border-top: 1px solid #20365c; }
  .bk-row .fl   { font-size: clamp(11px,1.2vw,23px); line-height: 1; }
  .bk-row .code { flex: 1; white-space: nowrap; }
  .bk-row .sc   { font-variant-numeric: tabular-nums; color: #8fa3c4; }
  .bk-row.w { background: linear-gradient(90deg, rgba(70,229,127,.18), transparent 72%); box-shadow: inset 3px 0 0 #46e57f; }
  .bk-row.w .sc { color: #46e57f; }
  .bk-row.l { opacity: .55; }
  .bk-row.tbd { color: #52688f; }
  .bk-match.spot { border-color: #FFB300; animation: bkGlow 1.6s ease infinite; }
  .bk-match.mkt  { border-color: rgba(255,138,60,.65); }
  .bk-col-label {
    text-align: center; font-size: clamp(7px,.75vw,15px); letter-spacing: .22em;
    color: #8fa3c4; font-weight: 700; font-family: 'Barlow Condensed', sans-serif;
    text-transform: uppercase; white-space: nowrap;
  }
  .bk-panel {
    background: rgba(10,20,38,.92); border: 1px solid #24406e; border-radius: 14px;
    padding: clamp(10px,1.6vh,20px); display: flex; flex-direction: column;
    align-items: center; gap: clamp(6px,1vh,12px); text-align: center;
    animation: bkPop .45s cubic-bezier(.34,1.3,.64,1) both;
  }
  .bk-leaderboard {
    position: fixed; top: 0; right: 0; height: 100vh; width: clamp(270px,25vw,440px);
    background: rgba(6,11,26,.94); border-left: 1px solid rgba(255,179,0,.15);
    transform: translateX(100%); transition: transform .4s cubic-bezier(.34,1.2,.64,1);
    z-index: 150; display: flex; flex-direction: column; padding: 18px 0;
  }
  .bk-leaderboard.open { transform: translateX(0); }
`;

// ─── Match card ───────────────────────────────────────────────────────────────
function MatchCard({ fixture, revealed, idx, quizIdx, marketIdxs, claimsOn, claimCount }) {
  const { home, away } = participantsOf(fixture, revealed, idx);
  const result = revealed?.[idx];
  const isQuiz = idx === quizIdx;      // spotlit tie — hide who won!
  const isMkt  = marketIdxs?.has(idx);
  // Claimable while the pairing isn't fully known — mirrors the engine rule
  const isClm  = claimsOn && !result && (!home || !away);

  const row = (code, side) => {
    const t = teamOf(fixture, code);
    const won  = result && result.winner === code;
    const lost = result && code && result.winner !== code;
    const score = result ? (side === 'home' ? result.homeScore : result.awayScore) : null;
    return (
      <div className={`bk-row${won ? ' w' : ''}${lost ? ' l' : ''}${!code ? ' tbd' : ''}`}>
        <span className="fl">{code ? t.flag : '·'}</span>
        <span className="code">{code || '———'}</span>
        {result && <span className="sc">{score}{won && result.pens ? 'ᴾ' : ''}</span>}
        {isMkt && !result && side === 'home' && <span style={{ fontSize: 10 }}>💰</span>}
        {isClm && side === 'home' && claimCount > 0 && (
          <span style={{ fontSize: 10, color: '#b48cff', fontWeight: 800 }}>🎯{claimCount}</span>
        )}
      </div>
    );
  };

  return (
    <div data-bk-idx={idx} className={`bk-match${isQuiz ? ' spot' : ''}${isMkt && !result ? ' mkt' : ''}${isClm ? ' clm' : ''}`}>
      <div className="bk-pos">{idx + 1}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {row(home, 'home')}
        {row(away, 'away')}
      </div>
    </div>
  );
}

// ─── Bracket grid (mirrored wings + center column) ────────────────────────────
function BracketGrid({ fixture, revealed, quizIdx, marketIdxs, claimsOn, claimCounts, centerPanel }) {
  const T      = fixture.teamCount;
  const counts = roundCounts(T);
  const starts = roundStarts(T);
  const R      = counts.length;          // number of rounds
  const names  = ROUND_NAMES[T] || [];
  const finalIdx = T - 2;                // last match index

  // Connector lines: the columns are fluid flexbox, so the elbows are drawn on
  // an SVG overlay from measured card positions rather than CSS borders.
  const containerRef = useRef(null);
  const rafRef       = useRef(null);
  const [links, setLinks] = useState({ w: 0, h: 0, paths: [] });

  const measureRef = useRef(() => {});
  measureRef.current = () => {
    const el = containerRef.current;
    if (!el) return;
    const crect = el.getBoundingClientRect();
    const sx = el.scrollLeft, sy = el.scrollTop;
    const rects = {};
    el.querySelectorAll('[data-bk-idx]').forEach(node => {
      const r = node.getBoundingClientRect();
      rects[node.dataset.bkIdx] = {
        left:  r.left  - crect.left + sx,
        right: r.right - crect.left + sx,
        cy:    r.top + r.height / 2 - crect.top + sy,
      };
    });
    const paths = [];
    for (let i = starts[1] ?? T; i < T - 1; i++) {
      const child = rects[i];
      for (const fi of feeders(T, i) || []) {
        const feeder = rects[fi];
        if (!child || !feeder) continue;
        // Wings flow toward the center, so the joining edge is whichever side
        // of the feeder faces its child.
        const toRight = feeder.left < child.left;
        const x1 = toRight ? feeder.right : feeder.left;
        const x2 = toRight ? child.left   : child.right;
        const mx = (x1 + x2) / 2;
        paths.push({
          key: `${fi}-${i}`,
          d:   `M${x1} ${feeder.cy} H${mx} V${child.cy} H${x2}`,
          on:  !!revealed?.[fi]?.winner,   // winner known → their path lights up
        });
      }
    }
    setLinks(prev => {
      const next = { w: el.scrollWidth, h: el.scrollHeight, paths };
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
  };

  const scheduleMeasure = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      measureRef.current();
    });
  }, []);

  // Center panels swap/grow between renders and shift the columns — re-measure
  // after every render (rAF-throttled; bails out when nothing moved).
  useEffect(() => { scheduleMeasure(); });
  useEffect(() => {
    const ro = new ResizeObserver(scheduleMeasure);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', scheduleMeasure);
    document.fonts?.ready?.then(scheduleMeasure).catch(() => {});
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      cancelAnimationFrame(rafRef.current);
    };
  }, [scheduleMeasure]);

  // Wing columns: every round except the final, split into halves.
  const wingRounds = [];
  for (let r = 0; r < R - 1; r++) {
    const idxs = Array.from({ length: counts[r] }, (_, j) => starts[r] + j);
    wingRounds.push({ name: names[r] || `Round ${r + 1}`, left: idxs.slice(0, idxs.length / 2), right: idxs.slice(idxs.length / 2) });
  }

  const col = (idxs, label, key) => (
    <div key={key} style={{
      display: 'flex', flexDirection: 'column', justifyContent: 'space-around',
      gap: 'clamp(4px,.8vh,10px)', flex: 1, minWidth: 0,
      position: 'relative', zIndex: 1, // cards paint above the connector overlay
    }}>
      <div className="bk-col-label">{label}</div>
      {idxs.map(i => <MatchCard key={i} fixture={fixture} revealed={revealed} idx={i} quizIdx={quizIdx}
        marketIdxs={marketIdxs} claimsOn={claimsOn} claimCount={claimCounts?.[i] || 0} />)}
    </div>
  );

  const champion = revealed?.[finalIdx]?.winner ? teamOf(fixture, revealed[finalIdx].winner) : null;

  return (
    <div ref={containerRef} style={{
      flex: 1, display: 'flex', gap: 'clamp(6px,.9vw,16px)', minHeight: 0,
      padding: 'clamp(6px,1vh,14px) clamp(8px,1.2vw,20px)', overflowX: 'auto',
      position: 'relative',
    }}>
      <svg width={links.w || 0} height={links.h || 0}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 0 }}>
        {links.paths.map(p => (
          <path key={p.key} d={p.d} fill="none"
            stroke={p.on ? 'rgba(70,229,127,.55)' : 'rgba(96,128,182,.30)'}
            strokeWidth={p.on ? 2 : 1.5}
            style={{ transition: 'stroke .5s, stroke-width .5s' }} />
        ))}
      </svg>
      {wingRounds.map((w, r) => col(w.left, w.name, `L${r}`))}

      {/* Center: final + champion + live panel */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 'clamp(8px,1.4vh,18px)', flex: '0 0 clamp(240px,26vw,380px)', minWidth: 0,
        position: 'relative', zIndex: 1,
      }}>
        <div className="bk-col-label">{names[R - 1] || 'Final'}</div>
        <div style={{ width: '72%' }}>
          <MatchCard fixture={fixture} revealed={revealed} idx={finalIdx} quizIdx={quizIdx}
            marketIdxs={marketIdxs} claimsOn={claimsOn} claimCount={claimCounts?.[finalIdx] || 0} />
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          border: `1.5px ${champion ? 'solid' : 'dashed'} rgba(242,201,76,.65)`,
          borderRadius: 8, padding: '6px 16px', color: '#f2c94c',
          fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 3,
          fontSize: 'clamp(13px,1.4vw,20px)',
          background: champion ? 'rgba(242,201,76,.1)' : 'transparent',
          animation: champion ? 'bkPop .6s cubic-bezier(.34,1.3,.64,1) both' : 'none',
        }}>
          <span style={{ fontSize: 'clamp(16px,1.8vw,26px)' }}>🏆</span>
          {champion ? <>{champion.flag} {champion.name.toUpperCase()}</> : 'CHAMPION'}
        </div>
        {centerPanel}
      </div>

      {[...wingRounds].reverse().map((w, i) => col(w.right, w.name, `R${wingRounds.length - 1 - i}`))}
    </div>
  );
}

// ─── Center panels ────────────────────────────────────────────────────────────
function QuizPanel({ quiz, pct, lockedCount }) {
  const side = (t) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 'clamp(28px,3.4vw,52px)', lineHeight: 1 }}>{t.flag}</div>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(15px,1.6vw,24px)', letterSpacing: 2 }}>{t.code}</div>
      <div style={{ fontSize: 'clamp(8px,.8vw,11px)', color: '#8fa3c4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{t.name}</div>
    </div>
  );
  return (
    <div className="bk-panel" style={{ width: '100%' }}>
      <div style={{ fontSize: 'clamp(8px,.8vw,11px)', letterSpacing: 4, color: '#FFB300',
        fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>
        {quiz.roundName?.toUpperCase()} — WHO WENT THROUGH?
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        {side(quiz.home)}
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(14px,1.5vw,22px)', color: '#52688f' }}>VS</div>
        {side(quiz.away)}
      </div>
      <div style={{ width: '100%', height: 6, background: 'rgba(255,255,255,.08)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct * 100}%`, borderRadius: 3,
          background: pct > .5 ? '#46e57f' : pct > .25 ? '#FFB300' : '#ff5252',
          transition: 'width .25s linear, background .5s' }} />
      </div>
      <div style={{ fontSize: 'clamp(9px,.9vw,13px)', color: '#8fa3c4' }}>
        📲 Text the team code — <b style={{ color: '#eaf1fb' }}>{quiz.home.code}</b> or <b style={{ color: '#eaf1fb' }}>{quiz.away.code}</b>
      </div>
      {lockedCount > 0 && (
        <div style={{ fontSize: 'clamp(9px,.95vw,13px)', color: '#46e57f', fontWeight: 700, animation: 'bkPulse 1.4s ease infinite' }}>
          🔒 {lockedCount} answer{lockedCount === 1 ? '' : 's'} locked in
        </div>
      )}
    </div>
  );
}

function RevealPanel({ reveal, fixture }) {
  const { result, home, away } = reveal;
  const winner = result.winner === home.code ? home : away;
  const winners = reveal.winners || [];
  const payouts = reveal.payouts || [];
  const top = (winners.length ? winners : payouts).slice(0, 5);
  return (
    <div className="bk-panel" style={{ width: '100%', borderColor: 'rgba(70,229,127,.5)' }}>
      <div style={{ fontSize: 'clamp(8px,.8vw,11px)', letterSpacing: 4, color: '#46e57f',
        fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>
        {reveal.source === 'market' ? 'MARKET SETTLED' : 'RESULT'}
      </div>
      <div style={{ fontSize: 'clamp(30px,3.6vw,56px)', lineHeight: 1, animation: 'bkPop .5s ease both' }}>{winner.flag}</div>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(18px,2vw,30px)', letterSpacing: 2 }}>
        {winner.name.toUpperCase()}
      </div>
      <div style={{ fontVariantNumeric: 'tabular-nums', color: '#8fa3c4', fontWeight: 700, fontSize: 'clamp(12px,1.2vw,17px)' }}>
        {home.code} {result.homeScore} – {result.awayScore} {away.code}{result.pens ? ' (pens)' : ''}
      </div>
      {top.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
          {top.map((w, i) => (
            <div key={w.jid || i} style={{ display: 'flex', gap: 8, alignItems: 'center',
              fontSize: 'clamp(9px,.95vw,13px)', animation: `bkSlideIn .3s ${i * .08}s ease both` }}>
              <span style={{ color: '#8fa3c4', minWidth: 16 }}>{i + 1}.</span>
              <span style={{ flex: 1, textAlign: 'left', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.name}</span>
              <span style={{ color: '#46e57f', fontWeight: 800 }}>+{w.points ?? w.profit} pts</span>
            </div>
          ))}
          {(winners.length || payouts.length) > 5 && (
            <div style={{ fontSize: 10, color: '#8fa3c4' }}>+{(winners.length || payouts.length) - 5} more</div>
          )}
        </div>
      )}
      {reveal.source === 'quiz' && winners.length === 0 && (
        <div style={{ fontSize: 'clamp(9px,.95vw,13px)', color: '#8fa3c4' }}>Nobody got it! 😅</div>
      )}
      {(reveal.claimWinners || []).length > 0 && (
        <div style={{ width: '100%', borderTop: '1px solid rgba(180,140,255,.25)', paddingTop: 6,
          display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ fontSize: 'clamp(8px,.8vw,11px)', letterSpacing: 3, color: '#b48cff',
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>
            🎯 CALLED IT EARLY
          </div>
          {reveal.claimWinners.slice(0, 3).map((c, i) => (
            <div key={c.jid || i} style={{ display: 'flex', gap: 8, alignItems: 'center',
              fontSize: 'clamp(9px,.95vw,13px)', animation: `bkSlideIn .3s ${i * .08}s ease both` }}>
              <span style={{ flex: 1, textAlign: 'left', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
              <span style={{ color: '#b48cff', fontWeight: 800 }}>+{c.points} pts</span>
            </div>
          ))}
          {reveal.claimWinners.length > 3 && (
            <div style={{ fontSize: 10, color: '#8fa3c4' }}>+{reveal.claimWinners.length - 3} more</div>
          )}
        </div>
      )}
    </div>
  );
}

function MarketsPanel({ markets, pools }) {
  return (
    <div className="bk-panel" style={{ width: '100%', maxHeight: '42vh', overflowY: 'auto', borderColor: 'rgba(255,138,60,.5)' }}>
      <div style={{ fontSize: 'clamp(8px,.8vw,11px)', letterSpacing: 4, color: '#ff8a3c',
        fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>
        💰 BETTING OPEN — TEXT: BET 200 {markets[0]?.home?.code || 'ESP'}
      </div>
      {markets.map(m => {
        const p = pools?.[m.matchIdx] || {};
        return (
          <div key={m.matchIdx} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            background: 'rgba(255,255,255,.04)', borderRadius: 8, padding: '5px 10px' }}>
            <span style={{ fontSize: 'clamp(12px,1.2vw,17px)' }}>{m.home.flag}</span>
            <span style={{ fontWeight: 800, fontSize: 'clamp(10px,1vw,14px)' }}>{m.home.code}</span>
            <span style={{ color: '#8fa3c4', fontSize: 'clamp(9px,.9vw,12px)', fontVariantNumeric: 'tabular-nums' }}>{p[m.home.code] || 0}</span>
            <span style={{ flex: 1, textAlign: 'center', color: '#52688f', fontSize: 10 }}>v</span>
            <span style={{ color: '#8fa3c4', fontSize: 'clamp(9px,.9vw,12px)', fontVariantNumeric: 'tabular-nums' }}>{p[m.away.code] || 0}</span>
            <span style={{ fontWeight: 800, fontSize: 'clamp(10px,1vw,14px)' }}>{m.away.code}</span>
            <span style={{ fontSize: 'clamp(12px,1.2vw,17px)' }}>{m.away.flag}</span>
          </div>
        );
      })}
      <div style={{ fontSize: 'clamp(8px,.85vw,11px)', color: '#8fa3c4' }}>
        Winners split the losing pool — settled on the real result
      </div>
    </div>
  );
}

function IdlePanel({ game, claimsOn }) {
  return (
    <div className="bk-panel" style={{ width: '100%', borderStyle: 'dashed' }}>
      <div style={{ fontSize: 'clamp(20px,2.2vw,32px)' }}>⚽</div>
      <div style={{ fontSize: 'clamp(9px,.95vw,13px)', color: '#8fa3c4', animation: 'bkPulse 2s ease infinite' }}>
        Waiting for the next round…
      </div>
      {claimsOn && (
        <div style={{ fontSize: 'clamp(8px,.85vw,11px)', color: '#b48cff' }}>
          🎯 Call a position early — text <b>5 ESP</b>
        </div>
      )}
      <div style={{ fontSize: 'clamp(8px,.85vw,11px)', color: '#52688f' }}>
        Join: <b style={{ color: '#8fa3c4' }}>/join {game?.id}</b>
      </div>
    </div>
  );
}

// ─── Waiting / completed screens ──────────────────────────────────────────────
function WaitingScreen({ game }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 'clamp(12px,2.4vh,26px)', animation: 'bkFadeIn .7s ease both',
      padding: 'clamp(12px,2.5vh,32px)' }}>
      <div style={{ fontSize: 'clamp(40px,7vh,80px)', animation: 'bkBounce 2s ease infinite' }}>🏆</div>
      <div style={{ fontSize: 'clamp(24px,5vh,52px)', fontWeight: 900, fontFamily: "'Bebas Neue',sans-serif",
        letterSpacing: 5, textShadow: '0 0 30px rgba(242,201,76,.5)', textAlign: 'center' }}>
        {game?.name || 'Road to the Final'}
      </div>
      <div style={{ fontSize: 'clamp(11px,1.6vh,16px)', letterSpacing: 4, color: 'rgba(242,201,76,.65)',
        fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>
        BRACKET CHALLENGE — STARTING SOON
      </div>
      <div style={{ background: 'rgba(242,201,76,.08)', border: '1px solid rgba(242,201,76,.25)',
        borderRadius: 16, padding: 'clamp(12px,2vh,20px) clamp(20px,3.5vw,40px)', textAlign: 'center' }}>
        <div style={{ fontSize: 'clamp(9px,1.1vw,13px)', letterSpacing: 4, color: 'rgba(242,201,76,.55)',
          fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 8 }}>GAME CODE</div>
        <div style={{ fontSize: 'clamp(28px,5.5vh,56px)', fontWeight: 900, fontFamily: "'Bebas Neue',sans-serif",
          letterSpacing: 10, color: '#f2c94c' }}>{game?.id || '——'}</div>
        <div style={{ fontSize: 'clamp(11px,1.4vh,14px)', color: 'rgba(255,255,255,.4)', marginTop: 4 }}>
          WhatsApp <strong>/join {game?.id}</strong> to play
        </div>
      </div>
      <div style={{ fontSize: 'clamp(10px,1.3vh,14px)', color: 'rgba(255,255,255,.35)', textAlign: 'center', maxWidth: 460, lineHeight: 1.5 }}>
        Each tie flashes up — text the team that really went through. Everyone correct
        in the window scores; the fastest score most. Bet on the matches still to be played —
        and call numbered positions early (text <b>5 ESP</b>) for big points at the reveal!
      </div>
    </div>
  );
}

function CompletedScreen({ game, scores }) {
  const top4 = (scores || []).filter(s => (s.score || 0) > 0).slice(0, 4);
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 20, animation: 'bkFadeIn .7s ease both', position: 'relative' }}>
      <Confetti />
      <div style={{ fontSize: 'clamp(60px,7vw,120px)' }}>🏆</div>
      <div style={{ fontSize: 'clamp(30px,5.5vw,104px)', fontWeight: 900, fontFamily: "'Bebas Neue',sans-serif",
        letterSpacing: 4, textShadow: '0 0 40px rgba(242,201,76,.8)' }}>Full Time!</div>
      {game?.winner && (
        <div style={{ background: 'linear-gradient(135deg, rgba(242,201,76,.2), rgba(255,140,0,.1))',
          border: '1px solid rgba(242,201,76,.4)', borderRadius: 20, padding: '14px 40px',
          textAlign: 'center', animation: 'bkPop .6s cubic-bezier(.34,1.2,.64,1) both' }}>
          <div style={{ fontSize: 'clamp(11px,1vw,18px)', letterSpacing: 4, color: 'rgba(242,201,76,.6)',
            fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 4 }}>CHAMPION TIPSTER</div>
          <div style={{ fontSize: 'clamp(22px,4vw,68px)', fontWeight: 900, fontFamily: "'Bebas Neue',sans-serif" }}>
            {game.winner.name}
          </div>
          <div style={{ fontSize: 'clamp(19px,2vw,36px)', color: '#f2c94c', fontWeight: 700 }}>{game.winner.score} pts</div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 'min(640px,84vw)' }}>
        {top4.map((s, i) => (
          <div key={s.jid || i} style={{ display: 'flex', alignItems: 'center', gap: 12,
            background: 'rgba(255,255,255,.05)', borderRadius: 12, padding: 'clamp(9px,1.1vw,18px) clamp(16px,1.6vw,28px)',
            opacity: s.left ? .4 : 1 }}>
            <div style={{ fontSize: 'clamp(20px,2vw,38px)', minWidth: 28, textAlign: 'center' }}>{['🥇', '🥈', '🥉'][i] || '4'}</div>
            <div style={{ flex: 1, fontWeight: 700, fontSize: 'clamp(15px,1.6vw,30px)' }}>{s.name}</div>
            <div style={{ color: '#f2c94c', fontWeight: 800, fontSize: 'clamp(15px,1.6vw,30px)' }}>{s.score}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Leaderboard panel ────────────────────────────────────────────────────────
function LeaderboardPanel({ scores, open, onClose }) {
  return (
    <div className={`bk-leaderboard${open ? ' open' : ''}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px 14px', borderBottom: '1px solid rgba(242,201,76,.12)' }}>
        <div style={{ fontSize: 'clamp(13px,1.3vw,22px)', fontWeight: 700, letterSpacing: 3,
          fontFamily: "'Barlow Condensed',sans-serif", color: 'rgba(242,201,76,.7)' }}>LEADERBOARD</div>
        {onClose && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.4)',
            cursor: 'pointer', fontSize: 18, minHeight: 44, minWidth: 44 }}>✕</button>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', paddingTop: 8 }}>
        {(scores || []).length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: 'rgba(255,255,255,.25)', fontSize: 13 }}>
            No players yet
          </div>
        ) : scores.map((s, i) => (
          <div key={s.jid || i} style={{ display: 'flex', alignItems: 'center', gap: 12,
            padding: '8px 18px', opacity: s.left ? .4 : 1 }}>
            <div style={{ width: 'clamp(26px,2.3vw,42px)', height: 'clamp(26px,2.3vw,42px)', borderRadius: 8, flexShrink: 0,
              background: i === 0 ? 'linear-gradient(135deg,#f2c94c,#FF8C00)' : 'rgba(255,255,255,.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: i < 3 ? 'clamp(13px,1.3vw,22px)' : 'clamp(11px,1.1vw,18px)', fontWeight: 900, color: i === 0 ? '#1a0a00' : 'rgba(255,255,255,.7)' }}>
              {i < 3 ? ['🥇', '🥈', '🥉'][i] : i + 1}
            </div>
            <div style={{ flex: 1, fontWeight: 600, fontSize: 'clamp(14px,1.4vw,24px)', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
            <div style={{ fontWeight: 800, fontSize: 'clamp(14px,1.4vw,26px)', color: '#f2c94c' }}>{s.score}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function BracketBoard({ gameId, onExit, onGameCompleted, tvMode }) {
  const [game,        setGame]        = useState(null);
  const [fixture,     setFixture]     = useState(null);
  const [revealed,    setRevealed]    = useState({});
  const [scores,      setScores]      = useState([]);
  const [quiz,        setQuiz]        = useState(null);    // {matchIdx, roundName, home, away, endsAt, windowSecs}
  const [pct,         setPct]         = useState(1);
  const [lockedCount, setLockedCount] = useState(0);
  const [reveal,      setReveal]      = useState(null);    // last match-revealed payload
  const [markets,     setMarkets]     = useState([]);
  const [pools,       setPools]       = useState({});
  const [claimCounts, setClaimCounts] = useState({});
  const [claimToast,  setClaimToast]  = useState(null);   // {name, pos, key}
  const toastRef = useRef(null);
  const [lbOpen,      setLbOpen]      = useState(false);
  const [loading,     setLoading]     = useState(true);

  const timerRef  = useRef(null);
  const revealRef = useRef(null);

  const startCountdown = useCallback((endsAt, windowSecs) => {
    if (timerRef.current) clearInterval(timerRef.current);
    const tick = () => {
      const remaining = Math.max(0, endsAt - Date.now());
      setPct(Math.min(1, remaining / (windowSecs * 1000)));
      if (remaining <= 0) clearInterval(timerRef.current);
    };
    tick();
    timerRef.current = setInterval(tick, 250);
  }, []);

  const hydrate = useCallback(async () => {
    try {
      const data = await fetch(`${API}/bracket/${gameId}`).then(r => r.json());
      if (data.error) return;
      setGame(data);
      setFixture(data.fixture || null);
      setRevealed(data.revealed || {});
      setClaimCounts(data.claimCounts || {});
      setScores(Object.values(data.scores || {}).sort((a, b) => (b.score || 0) - (a.score || 0)));

      // Mid-quiz reload: rebuild the spotlight from the light node
      if (data.status === 'active' && data.quizOpen && data.currentMatch >= 0 &&
          (data.quizEndsAt || 0) > Date.now() && data.fixture) {
        const fx = data.fixture;
        const { home, away } = participantsOf(fx, data.revealed || {}, data.currentMatch);
        const windowSecs = data.settings?.quizWindowSecs || 45;
        setQuiz({
          matchIdx: data.currentMatch,
          roundName: (ROUND_NAMES[fx.teamCount] || [])[roundOfMatch(fx.teamCount, data.currentMatch)],
          home: teamOf(fx, home), away: teamOf(fx, away),
          endsAt: data.quizEndsAt, windowSecs,
        });
        startCountdown(data.quizEndsAt, windowSecs);
      } else {
        setQuiz(null);
      }

      // Mid-betting reload: rebuild markets from the code → match index
      if (data.status === 'active' && data.phase === 'betting' && data.fixture && data.marketIndex) {
        const fx = data.fixture;
        const byMatch = {};
        for (const idx of new Set(Object.values(data.marketIndex))) {
          const { home, away } = participantsOf(fx, data.revealed || {}, idx);
          if (home && away) byMatch[idx] = {
            matchIdx: idx,
            roundName: (ROUND_NAMES[fx.teamCount] || [])[roundOfMatch(fx.teamCount, idx)],
            home: teamOf(fx, home), away: teamOf(fx, away),
          };
        }
        setMarkets(Object.values(byMatch).sort((a, b) => a.matchIdx - b.matchIdx));
      } else if (data.phase !== 'betting') {
        setMarkets([]);
      }
    } catch { /* board retries on next event */ }
  }, [gameId, startCountdown]);

  useEffect(() => {
    (async () => { await hydrate(); setLoading(false); })();
  }, [hydrate]);

  useEffect(() => () => { clearInterval(timerRef.current); clearTimeout(revealRef.current); clearTimeout(toastRef.current); }, []);

  useAblyChannel('crossword:admin', {
    'bracket-deleted': (data) => { if (data.gameId === gameId && onExit) onExit(); },
  });

  useAblyChannel(`bracket:game:${gameId}`, {
    'game-state': (data) => {
      setGame(g => ({ ...(g || {}), status: data.state }));
      if (data.state === 'active') hydrate();
      if (data.state === 'completed') {
        setQuiz(null); setMarkets([]);
        setScores(data.scores || []);
        if (data.winner) setGame(g => ({ ...(g || {}), winner: data.winner }));
        clearInterval(timerRef.current);
        // Let the podium screen breathe before the TV rotates away
        if (onGameCompleted) setTimeout(() => onGameCompleted(gameId, 'bracket'), 10000);
      }
    },
    'quiz-start': (data) => {
      setReveal(null); clearTimeout(revealRef.current);
      setMarkets([]); setLockedCount(0);
      setQuiz(data);
      startCountdown(data.endsAt, data.windowSecs);
    },
    'guess-locked': () => setLockedCount(c => c + 1),
    'claim-locked': (data) => {
      setClaimCounts(c => ({ ...c, [data.matchIdx]: (c[data.matchIdx] || 0) + 1 }));
      setClaimToast({ name: data.name, pos: data.matchIdx + 1, key: data.ts || Date.now() });
      clearTimeout(toastRef.current);
      toastRef.current = setTimeout(() => setClaimToast(null), 4500);
    },
    'match-revealed': (data) => {
      setRevealed(r => ({ ...r, [data.matchIdx]: data.result }));
      setQuiz(q => (q && q.matchIdx === data.matchIdx ? null : q));
      setScores(data.scores || []);
      setReveal(data);
      clearInterval(timerRef.current);
      clearTimeout(revealRef.current);
      revealRef.current = setTimeout(() => setReveal(null), 9000);
      if (data.source === 'market') {
        setMarkets(ms => ms.filter(m => m.matchIdx !== data.matchIdx));
      }
    },
    'markets-open': (data) => {
      setQuiz(null);
      setMarkets(data.markets || []);
    },
    'bet-placed': (data) => {
      setPools(p => {
        const match = { ...(p[data.matchIdx] || {}) };
        match[data.code] = (match[data.code] || 0) + data.amount;
        return { ...p, [data.matchIdx]: match };
      });
    },
    'score-update': (data) => setScores(data.scores || []),
    'player-joined': () => {},
  });

  if (loading) {
    return (
      <div className="bk-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <style>{STYLES}</style>
        <div style={{ animation: 'bkSpin 1s linear infinite', fontSize: 40 }}>⚙️</div>
      </div>
    );
  }

  const status = game?.status || 'waiting';
  const marketIdxs = new Set(markets.map(m => m.matchIdx));
  const claimsOn = game?.settings?.claimsEnabled !== false;

  const centerPanel =
    reveal          ? <RevealPanel reveal={reveal} fixture={fixture} /> :
    quiz            ? <QuizPanel quiz={quiz} pct={pct} lockedCount={lockedCount} /> :
    markets.length  ? <MarketsPanel markets={markets} pools={pools} /> :
                      <IdlePanel game={game} claimsOn={claimsOn} />;

  return (
    <div className="bk-root">
      <style>{STYLES}</style>

      {/* Header strip */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'clamp(6px,1vh,12px) clamp(12px,1.6vw,24px)',
        borderBottom: '1px solid rgba(242,201,76,.12)', flexShrink: 0 }}>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 4,
          fontSize: 'clamp(15px,1.8vw,26px)' }}>
          ROAD TO THE <span style={{ color: '#46e57f' }}>FINAL</span>
        </div>
        <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: 3,
          fontSize: 'clamp(9px,1vw,13px)', color: '#8fa3c4' }}>
          {game?.name?.toUpperCase()} · JOIN: /JOIN {gameId}
        </div>
      </div>

      {/* Main content */}
      <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {status === 'waiting' && <WaitingScreen game={game} />}
        {status === 'active' && fixture && (
          <BracketGrid fixture={fixture} revealed={revealed} quizIdx={quiz?.matchIdx ?? -1}
            marketIdxs={marketIdxs} claimsOn={claimsOn} claimCounts={claimCounts} centerPanel={centerPanel} />
        )}
        {status === 'active' && !fixture && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ animation: 'bkPulse 1.5s ease infinite', fontSize: 13, color: 'rgba(255,255,255,.4)' }}>
              Loading bracket…
            </div>
          </div>
        )}
        {status === 'completed' && <CompletedScreen game={game} scores={scores} />}
      </div>

      {claimToast && (
        <div key={claimToast.key} style={{
          position: 'fixed', bottom: 'clamp(58px,9vh,92px)', left: '50%', zIndex: 250,
          background: 'rgba(26,17,46,.95)', border: '1px solid rgba(180,140,255,.55)',
          borderRadius: 999, padding: 'clamp(6px,.9vh,10px) clamp(16px,1.8vw,26px)',
          fontSize: 'clamp(12px,1.2vw,16px)', fontWeight: 700, whiteSpace: 'nowrap',
          boxShadow: '0 6px 24px rgba(0,0,0,.45)',
          animation: 'bkToast .4s cubic-bezier(.34,1.3,.64,1) both',
        }}>
          🎯 <b style={{ color: '#b48cff' }}>{claimToast.name}</b> called position {claimToast.pos}!
        </div>
      )}

      <LeaderboardPanel scores={scores} open={lbOpen} onClose={() => setLbOpen(false)} />

      {/* Nav bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'clamp(5px,.8vh,10px) clamp(10px,1.5vw,18px)',
        background: 'rgba(0,0,0,.4)', borderTop: '1px solid rgba(255,255,255,.05)', flexShrink: 0 }}>
        <button onClick={() => setLbOpen(o => !o)} style={{
          background: 'rgba(242,201,76,.1)', border: '1px solid rgba(242,201,76,.3)',
          borderRadius: 8, color: '#f2c94c', fontSize: 'clamp(11px,1.1vw,14px)', fontWeight: 700,
          fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 3,
          padding: 'clamp(8px,1.1vh,12px) clamp(12px,1.5vw,20px)', cursor: 'pointer', minHeight: 44 }}>
          {lbOpen ? 'HIDE' : '📊 SCORES'}
        </button>
        {!tvMode && onExit && (
          <button onClick={onExit} style={{
            background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)',
            borderRadius: 8, color: 'rgba(255,255,255,.4)', fontSize: 'clamp(11px,1.1vw,14px)', fontWeight: 700,
            fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 3,
            padding: 'clamp(8px,1.1vh,12px) clamp(12px,1.5vw,20px)', cursor: 'pointer', minHeight: 44 }}>
            ← EXIT
          </button>
        )}
      </div>
    </div>
  );
}
